import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { getAllPayments } from "../service/AdminService";
import { getFoods } from "../service/CatalogService";
import { getMyOrders } from "../service/OrderService";
import { Payment, FoodItem, Order } from "../model/types";

const monthName = (d: Date) =>
  d.toLocaleString(undefined, { month: "short", year: "numeric" });

export const Home = () => {
  const { user } = useAuth();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [foods, setFoods] = useState<FoodItem[]>([]);
  const [filter, setFilter] = useState<"ALL" | "AVAILABLE" | "OUT">("ALL");
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingFoods, setLoadingFoods] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      setLoadingPayments(true);
      try {
        const data = await getAllPayments();
        setPayments(data || []);
      } catch (e) {
        setPayments([]);
      } finally {
        setLoadingPayments(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadFoods = async () => {
      setLoadingFoods(true);
      try {
        // for availability filtering we fetch all then filter client-side for "OUT"
        const data = await getFoods(false);
        setFoods(data || []);
      } catch (e) {
        setFoods([]);
      } finally {
        setLoadingFoods(false);
      }
    };
    loadFoods();
  }, []);

  useEffect(() => {
    if (user?.role !== "CUSTOMER") return;
    const load = async () => {
      setLoadingOrders(true);
      try {
        const data = await getMyOrders();
        setOrders(data || []);
      } catch (e) {
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };
    load();
  }, [user?.role]);

  // compute income for last 3 months (including current month)
  const incomeByMonth = useMemo(() => {
    const now = new Date();
    const months: { label: string; start: Date; end: Date; amount: number }[] = [];
    for (let i = 2; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0);
      months.push({ label: monthName(d), start, end, amount: 0 });
    }

    payments.forEach((p) => {
      if (!p.paidAt) return;
      const paid = new Date(p.paidAt);
      months.forEach((m) => {
        if (paid >= m.start && paid < m.end && p.status === "COMPLETED") {
          m.amount += p.amount;
        }
      });
    });

    return months;
  }, [payments]);

  const filteredFoods = useMemo(() => {
    if (filter === "ALL") return foods;
    if (filter === "AVAILABLE") return foods.filter((f) => f.status === "AVAILABLE");
    return foods.filter((f) => f.status === "OUT_OF_STOCK");
  }, [foods, filter]);

  const filteredOrders = useMemo(() => {
    if (!startDate && !endDate) return orders;
    const start = startDate ? new Date(startDate) : new Date("1970-01-01");
    const end = endDate ? new Date(endDate + "T23:59:59") : new Date();
    return orders.filter((o) => {
      const created = new Date(o.createdAt);
      return created >= start && created <= end;
    });
  }, [orders, startDate, endDate]);

  return (
    <section className="page-shell space-y-6">
      <div className="rounded-3xl bg-slate-900 px-6 py-8 text-white shadow-sm">
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-300">
          Dashboard
        </p>
        <h2 className="mt-2 text-3xl font-semibold">
          Welcome back, {user?.fullName}.
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-slate-300">
          {user?.role === "ADMIN"
            ? "Use the management screens to maintain categories, menu items, orders, payments, and user records."
            : "Browse the menu, manage your cart, place orders, and track payments from one place."}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">

        {user?.role === "ADMIN" && (

          <>
          
          <article className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Signed In As</p>
            <h3 className="mt-3 text-lg font-semibold text-slate-900">{user?.role}</h3>
            <p className="mt-2 text-sm text-stone-600">{user?.email}</p>
          </article>


            <article className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
              <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Income (last 3 months)</p>
              <h3 className="mt-3 text-lg font-semibold text-emerald-700">
                {loadingPayments ? "Loading…" : "Income summary"}
              </h3>
              <div className="mt-3 text-sm text-stone-600 space-y-2">
                {incomeByMonth.map((m) => (
                  <div key={m.label} className="flex items-center justify-between">
                    <span className="text-sm text-stone-700">{m.label}</span>
                    <span className="font-medium text-slate-900">LKR {m.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Inventory</p>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900">Food items & counts</h3>
                </div>
                <div>
                  <label className="text-xs text-stone-500 mr-2">Filter</label>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="ALL">All</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="OUT">Out of stock</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 text-sm text-stone-600">
                {loadingFoods ? (
                  <div>Loading…</div>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-auto">
                    {filteredFoods.map((f) => (
                      <div key={f.id} className="flex items-center justify-between border-b py-2">
                        <div>
                          <div className="font-medium text-slate-900">{f.name}</div>
                          <div className="text-xs text-stone-500">{f.categoryName}</div>
                        </div>
                        <div className="text-sm">
                          <span className="mr-3">{f.stockQuantity}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${f.status === "AVAILABLE" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                            {f.status === "AVAILABLE" ? "Available" : "Out"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {filteredFoods.length === 0 && <div className="text-xs text-stone-500">No items to show.</div>}
                  </div>
                )}
              </div>
            </article>
          </>
        )}
      </div>

      {user?.role === "CUSTOMER" && (
        <article className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Your Orders</p>
              <h3 className="mt-3 text-lg font-semibold text-slate-900">Recent orders & status</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col text-sm">
                <label className="text-xs text-stone-500">From</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded px-2 py-1" />
              </div>
              <div className="flex flex-col text-sm">
                <label className="text-xs text-stone-500">To</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded px-2 py-1" />
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-stone-600">
            {loadingOrders ? (
              <div>Loading orders…</div>
            ) : (
              <div className="space-y-3 max-h-60 overflow-auto">
                {filteredOrders.map((o) => (
                  <div key={o.orderId} className="flex items-center justify-between rounded-2xl bg-stone-50 px-4 py-3">
                    <div>
                      <div className="font-medium text-slate-900">Order ID: {o.orderId}</div>
                      <div className="text-xs text-stone-500">{new Date(o.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">Rs. {o.totalAmount.toFixed(2)}</div>
                      <div className="mt-1">
                        <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-stone-700 mr-2">{o.status}</span>
                        <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-amber-700">{o.paymentStatus ?? 'NO_PAYMENT'}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {!filteredOrders.length && <div className="text-xs text-stone-500">No orders found for selected dates.</div>}
              </div>
            )}
          </div>
        </article>
      )}
    </section>
  );
};
